import React, { useState, useEffect } from 'react';
import { ChevronDown, Wrench, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { getMyMusicianProfile, updateBookingGearOverride } from '../../lib/api';
import { Button } from '../ui/button';
import { broughtCategories, gearShortLabel } from '../../constants/gearCategories';
import GearEditor from '../GearEditor';

/**
 * "Gear for this gig" — lets the talent override their profile gear defaults
 * for one booking. Null override = use profile defaults.
 *
 * Self-contained: pulls the musician's own profile gear (defaults) on mount.
 */
export default function GigGearPanel({ bookingId, gearOverride }) {
  const [open, setOpen] = useState(false);
  const [profileGear, setProfileGear] = useState([]);
  const [override, setOverride] = useState(gearOverride ?? null);
  const [working, setWorking] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    getMyMusicianProfile()
      .then((res) => { if (alive) setProfileGear(res.data?.gear_profile || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const usingDefaults = override == null;
  const resolved = usingDefaults ? profileGear : override;

  const expand = () => {
    setWorking(resolved.map((g) => ({ ...g })));
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateBookingGearOverride(bookingId, working);
      setOverride(res.data?.gear_override ?? working);
      toast.success('Gear updated for this gig');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update gear');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    setSaving(true);
    try {
      await updateBookingGearOverride(bookingId, null);
      setOverride(null);
      setWorking(profileGear.map((g) => ({ ...g })));
      toast.success('Reset to your profile defaults');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset gear');
    } finally {
      setSaving(false);
    }
  };

  const broughtTags = broughtCategories(resolved);

  return (
    <div
      className="mb-4"
      style={{ background: 'var(--s8-bg-surface)', border: '1px solid var(--s8-border-card)', borderRadius: 'var(--s8-radius-lg)' }}
      data-testid="gig-gear-panel"
    >
      <button
        onClick={() => (open ? setOpen(false) : expand())}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="gig-gear-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Wrench className="h-4 w-4 shrink-0" style={{ color: 'var(--s8-amber, #C8923E)' }} />
          <span className="uppercase shrink-0" style={{ fontFamily: 'var(--s8-font-condensed)', letterSpacing: '0.06em', fontSize: '13px', color: 'var(--s8-text-main)' }}>
            Gear for this gig
          </span>
          <span className="truncate" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: usingDefaults ? 'var(--s8-text-muted)' : 'var(--s8-amber, #C8923E)' }}>
            · {usingDefaults ? 'Using your profile defaults' : 'Custom for this gig'}
          </span>
        </div>
        <ChevronDown className="h-4 w-4 shrink-0 transition-transform" style={{ color: 'var(--s8-text-muted)', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {!open && broughtTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-3">
          {broughtTags.map((cat) => (
            <span key={cat} className="s8-tag" style={{ fontFamily: 'var(--s8-font-mono)', color: 'var(--green-ok, #5A9A6B)', borderColor: 'var(--green-ok, #5A9A6B)' }}>
              {gearShortLabel(cat)}
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="px-4 pb-4 pt-1 border-t" style={{ borderColor: 'var(--s8-border-card)' }}>
          {!loaded ? (
            <div className="h-16 skeleton-pulse" />
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-3">
                Pre-filled from your profile. Changes apply to this gig only.
              </p>
              <GearEditor value={working} onChange={setWorking} disabled={saving} />
              <div className="flex items-center gap-2 mt-4">
                <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="gig-gear-save">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save for this gig'}
                </Button>
                {!usingDefaults && (
                  <Button onClick={resetToDefaults} disabled={saving} variant="outline" className="border-border" data-testid="gig-gear-reset">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset to profile defaults
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
